/** LocalStorage key for the signed DTIMS user context JWT. */
export const USER_CONTEXT_TOKEN_KEY: string = 'dtims_user_context_token';

/**
 * JWT claim keys minted by the backend RequestContextJwtProvider.
 * The envId claim is mapped to UserContextClaims.environmentId.
 */
export const USER_CONTEXT_JWT_CLAIM_KEYS: {
  readonly tenantId: 'tenantId';
  readonly environmentId: 'envId';
  readonly userId: 'userId';
} = {
  tenantId: 'tenantId',
  environmentId: 'envId',
  userId: 'userId',
} as const;

/**
 * Tenant and environment scope for the signed-in session, extracted from the user context JWT.
 * Shared across shell, licensing, CAR, and other tenant-scoped apps (not platform-admin).
 */
export interface UserContextClaims {
  /** Tenant id claim (`tenantId`). */
  tenantId: string;
  /** Environment id claim (`envId`). */
  environmentId: string;
  /** Platform user id claim (`userId`), when the token includes it. */
  userId?: string;
}

/**
 * Decodes the payload segment of a JWT without verifying the signature.
 * @param token - The raw JWT string.
 * @returns The decoded payload claims, or null when the token is malformed.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts: Array<string> = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    return JSON.parse(decodeBase64Url(parts[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Reads tenant and environment identifiers from the persisted user context JWT.
 * @param tokenOverride - Optional raw JWT. When omitted, reads from localStorage.
 * @returns The resolved claims, or null when the token is missing or incomplete.
 */
export function getUserContextClaims(tokenOverride?: string | null): UserContextClaims | null {
  const token: string | null =
    tokenOverride !== undefined
      ? tokenOverride
      : typeof localStorage !== 'undefined'
        ? localStorage.getItem(USER_CONTEXT_TOKEN_KEY)
        : null;

  if (!token) {
    return null;
  }

  const payload: Record<string, unknown> | null = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  const tenantId: string | null = readStringClaim(payload, USER_CONTEXT_JWT_CLAIM_KEYS.tenantId);
  const environmentId: string | null = readStringClaim(payload, USER_CONTEXT_JWT_CLAIM_KEYS.environmentId);

  if (!tenantId || !environmentId) {
    return null;
  }

  const userId: string | null = readStringClaim(payload, USER_CONTEXT_JWT_CLAIM_KEYS.userId);
  if (!userId) {
    return { tenantId, environmentId };
  }

  return { tenantId, environmentId, userId };
}

function decodeBase64Url(value: string): string {
  const base64: string = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength: number = (4 - (base64.length % 4)) % 4;
  const paddedBase64: string = `${base64}${'='.repeat(paddingLength)}`;

  return atob(paddedBase64);
}

function readStringClaim(payload: Record<string, unknown>, key: string): string | null {
  const value: unknown = payload[key];

  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return null;
}
